import { useQuery, UseQueryResult } from "@tanstack/react-query"
import { carrotClient } from "#/utils/CarrotClient"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { getHeap } from "#/utils/heap"
import ResponseError from "#/types/responseError"
import type { HelpCenterCategory, HelpCenterResponse } from "./types"

const HELP_CENTER_QUERY_KEY = ["help-center"] as const

async function loadHelpCenter(): Promise<HelpCenterResponse> {
  const beforeFetch = performance.now()
  const response = await carrotClient.getHelpCenter()
  const afterFetch = performance.now()

  getHeap().track("Help Center Tree Load Time", {
    "Help Center Tree Load Time In Milliseconds": afterFetch - beforeFetch
  })

  return response
}

function useGetHelpCenter<TData = HelpCenterResponse>(
  select?: (data: HelpCenterResponse) => TData
): UseQueryResult<TData, ResponseError> {
  return useQuery<HelpCenterResponse, ResponseError, TData>({
    queryKey: HELP_CENTER_QUERY_KEY,
    queryFn: loadHelpCenter,
    refetchOnWindowFocus: false,
    retry: buildCarrotApiRetryPolicy(3),
    staleTime: 1000 * 60 * 5,
    select
  })
}

export function useHelpCenterCategories(): UseQueryResult<HelpCenterCategory[], ResponseError> {
  return useGetHelpCenter(({ categories }) => sortCategories(categories))
}

export function useHelpCenterCategoryBySlug(
  slug: string
): UseQueryResult<HelpCenterCategory | undefined, ResponseError> {
  return useGetHelpCenter(({ categories }) => categories.find((category) => category.slug === slug))
}

function sortCategories(categories: HelpCenterCategory[]): HelpCenterCategory[] {
  return [...categories].sort((a, b) => {
    const aOrder = a.order ?? Number.POSITIVE_INFINITY
    const bOrder = b.order ?? Number.POSITIVE_INFINITY
    if (aOrder !== bOrder) return aOrder - bOrder
    return a.title.localeCompare(b.title)
  })
}
